import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { getItem, setItem } from '../../../utility/localStorageControl';
import { useNotifications } from '../../../hooks/useNotifications';
import { WorkStationRecordsService } from '../../../services/workStationRecordsService';
import { WorkStationsSettingsService } from '../../../services/workStationsSettingsService';
import { WorkStationsService } from '../../../services/workStationsService';

const CHECKIN_DATE_KEY = 'workStationCheckinDate';
const CHECK_INTERVAL_MS = 10 * 60 * 1000;

export const useWorkStationCheckinVisibility = ({ show }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { showErrorNotification } = useNotifications();

  const checkShouldAskOperator = useCallback(async () => {
    const result = { shouldAskOperator: false, hasError: false };
    try {
      const { workStationsSettings } = await WorkStationsSettingsService.getSettings();
      result.shouldAskOperator = workStationsSettings?.askOperatorWorkStation;
    } catch (error) {
      console.error('Error retrieving work station settings:', error);
      result.hasError = true;
    }
    return result;
  }, []);

  const checkHasAssignedWorkStation = useCallback(async () => {
    const result = { hasAssignedWorkStation: false, hasError: false };
    try {
      const { workStation } = await WorkStationsService.getCurrent();
      result.hasAssignedWorkStation = workStation != null;
    } catch (error) {
      console.error('Error retrieving current work station:', error);
      result.hasError = true;
    }
    return result;
  }, []);

  const checkStoredCheckinDate = useCallback(() => {
    const minDate = moment().subtract(12, 'hours').toISOString();
    const storedDate = getItem(CHECKIN_DATE_KEY);
    const result = { hasResolved: false, shouldShow: false };
    if (storedDate > minDate) {
      result.hasResolved = true;
      result.shouldShow = false;
    } else if (storedDate === null) {
      result.hasResolved = true;
      result.shouldShow = true;
    }
    return result;
  }, []);

  const checkLastRecord = useCallback(async () => {
    const minDate = moment().subtract(12, 'hours').toISOString();
    const result = { hasRecentRecord: false, hasError: false };
    try {
      const { workStationRecord } = await WorkStationRecordsService.getLastRecord();
      const hasRecentRecord = workStationRecord != null && workStationRecord.createdAt > minDate;
      if (hasRecentRecord) {
        setItem(CHECKIN_DATE_KEY, workStationRecord.createdAt);
      }
      result.hasRecentRecord = hasRecentRecord;
    } catch {
      showErrorNotification({ title: 'error', description: 'errorRetrievingRecord' });
      result.hasError = true;
    }
    return result;
  }, [showErrorNotification]);

  const runCheck = useCallback(async () => {
    if (!show) {
      setIsVisible(false);
      return;
    }

    const settingsResult = await checkShouldAskOperator();
    if (settingsResult.hasError) return;
    if (!settingsResult.shouldAskOperator) {
      setIsVisible(false);
      return;
    }

    const workStationResult = await checkHasAssignedWorkStation();
    if (workStationResult.hasError) return;
    if (!workStationResult.hasAssignedWorkStation) {
      setIsVisible(true);
      return;
    }

    const storedCheckinResult = checkStoredCheckinDate();
    if (storedCheckinResult.hasResolved) {
      setIsVisible(storedCheckinResult.shouldShow);
      return;
    }

    const recordResult = await checkLastRecord();
    if (recordResult.hasError) return;
    setIsVisible(!recordResult.hasRecentRecord);
  }, [show, checkShouldAskOperator, checkHasAssignedWorkStation, checkStoredCheckinDate, checkLastRecord]);

  useEffect(() => {
    runCheck();
    const intervalId = setInterval(runCheck, CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [runCheck]);

  const markCheckedIn = useCallback(() => {
    setItem(CHECKIN_DATE_KEY, moment().toISOString());
  }, []);

  const closeModal = useCallback(() => {
    setIsVisible(false);
  }, []);

  return { isVisible, markCheckedIn, closeModal };
};
